import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { b as businessCoreInfoSchema } from './business-core-info-schema.js';
import { F as FormController } from './form.js';
import { C as CoreBusinessInfo } from './Business.js';
import { B as BusinessFormStepV2 } from './business-form-types.js';
import { a as businessClassificationOptions } from './business-form-options.js';
import { P as PHONE_MASKS } from './form-input-masks.js';
import { n as numberOnlyHandler } from './utils4.js';
import { d as defineCustomElement$7 } from './custom-popper2.js';
import { d as defineCustomElement$6 } from './form-control-date2.js';
import { d as defineCustomElement$5 } from './form-control-error-text2.js';
import { d as defineCustomElement$4 } from './form-control-number-masked2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const BusinessCoreInfoFormStepCore = /*@__PURE__*/ proxyCustomElement(class BusinessCoreInfoFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    this.coreInfo = new CoreBusinessInfo(response.data);
                    this.formController.setInitialValues(Object.assign({}, this.coreInfo));
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.formLoading.emit(false)
            });
        };
        this.sendData = (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: this.patchPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: { submittedData } });
                    this.stepCompleted.emit({ data: submittedData, formStep: BusinessFormStepV2.businessInfo });
                    this.formLoading.emit(false);
                }
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.coreInfo = {};
        this.businessId = undefined;
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    get patchPayload() {
        let formValues = new CoreBusinessInfo(this.formController.values.getValue()).payload;
        return formValues;
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(businessCoreInfoSchema(this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        const coreInfoDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: 'd3df5d7e22f53b5885545459a48e1931f8a38e31' }, h("fieldset", { key: '5a448358ef2ad53c597347f932ca51a5da2ba4e6' }, h("div", { key: '5ed392c4bb7f63258c60b13d83aa5325fb191e7c', class: "d-flex align-items-center gap-2" }, h("legend", { key: '2dada21c67d05b813529eb45cb105ccec547b620', class: "mb-0" }, "Business Information")), h("hr", { key: '9df6c05b6f34d8fd2154ef25bda1ec2a88f69bc7', class: "mt-2" }), h("div", { key: 'd9e8e3c87a9f3c41c325ed8b3df58443db026644', class: "row gy-3" }, h("div", { key: '7e940fb2b9befb51be4387d47498ecb8e3b1552a', class: "col-12" }, h("form-control-text", { key: '4ea4b4aae5b1e377dc45407e4d0f6f8277f74dc3', name: "legal_name", label: "Business Name", defaultValue: coreInfoDefaultValue.legal_name, errorText: this.errors.legal_name, inputHandler: this.inputHandler, helpText: "Enter this exactly as it appears on your tax records (don't use acronyms or abbreviations unless you registered that way)." })), h("div", { key: 'ec86bf16281d8d8c0c954606e4fc7fb1d8b2507e', class: "col-12" }, h("form-control-text", { key: 'bef91169dea8a654faf8df3abed565a64275aa9d', name: "doing_business_as", label: "Doing Business As (DBA)", defaultValue: coreInfoDefaultValue.doing_business_as, errorText: this.errors.doing_business_as, inputHandler: this.inputHandler, helpText: "Enter this exactly as it appears on your tax records (leave blank if you don't have a registered DBA/trade name)" })), h("div", { key: '5568cecd8e6e3d2ab2a45d4eff583c1dfba2f07a', class: "col-12 col-md-8" }, h("form-control-select", { key: '96b2eea102e0686910af7950742256d6019d8ef9', name: "classification", label: "Business Classification", options: businessClassificationOptions, defaultValue: coreInfoDefaultValue.classification, errorText: this.errors.classification, inputHandler: this.inputHandler })), h("div", { key: '1fad6738d6c89a184b9f2e41eaef095db87471d6', class: "col-12 col-md-4" }, h("form-control-date", { key: 'cd10c2b8091343b0a8d7b08ceddf4343803333f6', name: "date_of_incorporation", label: "Date of Incorporation", defaultValue: coreInfoDefaultValue.date_of_incorporation, errorText: this.errors.date_of_incorporation, inputHandler: this.inputHandler })), h("div", { key: '3786fa9ce6b85c70873a4ed0014ef7c9b4a786f8', class: "col-12 col-md-6" }, h("form-control-text", { key: 'c5374dedf05acc1c9874061d796ee704160745a0', name: "industry", label: "Industry", defaultValue: coreInfoDefaultValue.industry, errorText: this.errors.industry, inputHandler: this.inputHandler, helpText: "Describe what you sell" })), h("div", { key: '0f30b590c250a6ac1beaaf1cc4a3eefeb394f3b0', class: "col-12 col-md-6" }, h("form-control-text", { key: '983b05cd0bf9aca0572b6f328e26b0c6f04ea9ee', name: "tax_id", label: "Tax ID (EIN or SSN)", defaultValue: coreInfoDefaultValue.tax_id, errorText: this.errors.tax_id, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, maxLength: 9, helpText: "Employer Identification Numbers (EINs) are nine digits. The federal tax identification number/EIN issued to you by the IRS. It can be found on your tax returns." })), h("div", { key: 'beacf611410f74b8038919887cc20c206bd5edce', class: "col-12" }, h("form-control-text", { key: '14a69d2ecedfcadf613aa42625b12405fba67068', name: "website_url", label: "Business Website URL", defaultValue: coreInfoDefaultValue.website_url, errorText: this.errors.website_url, inputHandler: this.inputHandler, helpText: "Don't have a live website? You can use your social media business page, app store link, or staging site URL." })), h("div", { key: '17171947a231323d63c42aa2c0a645aba91926d8', class: "col-12 col-md-6" }, h("form-control-text", { key: 'd0e32c0eb8aeb1c661f48f993a0cf44b4f2cf5b3', name: "email", label: "Business Email Address", defaultValue: coreInfoDefaultValue.email, errorText: this.errors.email, inputHandler: this.inputHandler })), h("div", { key: '1a8ee5ca6825ee3046fd538871c8c01ae561e731', class: "col-12 col-md-6" }, h("form-control-number-masked", { key: 'b74ff08dc5d97a66ff7d41fcdc8a2964e8739430', name: "phone", label: "Business Phone Number", defaultValue: coreInfoDefaultValue.phone, errorText: this.errors.phone, inputHandler: this.inputHandler, mask: PHONE_MASKS.US }))))));
    }
}, [0, "justifi-business-core-info-form-step-core", {
        "businessId": [1, "business-id"],
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "coreInfo": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-core-info-form-step-core", "custom-popper", "form-control-date", "form-control-error-text", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-core-info-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessCoreInfoFormStepCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessCoreInfoFormStepCore as B, defineCustomElement as d };
